import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  AcademicCapIcon,
  ClockIcon,
  CalendarDaysIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { getLearningProgress, updateLearningProgress } from '../utils/learningProgressStorage';
import { getPracticeAchievements, getMockSummary, masteryLabel } from '../utils/practiceAchievementStorage';
import { examData } from '../data/examData';
import CommonHeader from './common/CommonHeader';

const LearningProgressTracker = () => {
  const { user, isAuthenticated } = useAuth();
  const [progressData, setProgressData] = useState(null);
  const [achievements, setAchievements] = useState({ mockExams: [], subjects: {} });
  const [activeTab, setActiveTab] = useState('overview'); // overview, subjects, pattern, goals
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProgressData = async () => {
      if (!isAuthenticated || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        const progress = getLearningProgress(user.id);
        setProgressData(progress);
        setAchievements(getPracticeAchievements(user.id));
      } catch (error) {
        console.error('학습 진도 데이터 로딩 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProgressData();
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">학습 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-auto">
            <AcademicCapIcon className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">학습 기록이 없습니다</h2>
            <p className="text-gray-600 mb-6">CBT 문제를 풀어보시면 자동으로 학습 진도가 기록됩니다.</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              문제 풀러 가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabButtons = [
    { key: 'overview', label: '전체 현황', icon: ChartBarIcon },
    { key: 'subjects', label: '과목별 진도', icon: AcademicCapIcon },
    { key: 'pattern', label: '학습 패턴', icon: ClockIcon },
    { key: 'goals', label: '목표 관리', icon: TrophyIcon }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <CommonHeader 
        title="학습 진도"
        subtitle="상세한 학습 현황과 진도를 확인하세요"
      />

      {/* 탭 네비게이션 */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto">
            {tabButtons.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 컨텐츠 영역 */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && <OverviewTab progressData={progressData} achievements={achievements} />}
        {activeTab === 'subjects' && <SubjectsTab progressData={progressData} achievements={achievements} />}
        {activeTab === 'pattern' && <PatternTab progressData={progressData} />}
        {activeTab === 'goals' && <GoalsTab progressData={progressData} />}
      </div>
    </div>
  );
};

// 색상 매핑 (Tailwind 정적 클래스 유지용)
const COLOR_MAP = {
  green: { bar: 'bg-green-500', text: 'text-green-600', badge: 'bg-green-100 text-green-700', soft: 'bg-green-50' },
  blue: { bar: 'bg-blue-500', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', soft: 'bg-blue-50' },
  amber: { bar: 'bg-amber-500', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', soft: 'bg-amber-50' },
  red: { bar: 'bg-red-500', text: 'text-red-600', badge: 'bg-red-100 text-red-700', soft: 'bg-red-50' },
};

// 모의고사 성취도 카드
const MockExamAchievementCard = ({ mockExams }) => {
  const summary = getMockSummary(mockExams);

  if (!summary) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
          <TrophyIcon className="w-6 h-6 text-yellow-500 mr-2" />
          모의고사 성취도
        </h3>
        <p className="text-gray-500 text-sm mt-3">
          아직 응시한 실전 모의고사가 없습니다. 자격증 페이지에서 <span className="font-semibold text-orange-600">실전 모의고사</span>에 도전해 보세요!
        </p>
      </div>
    );
  }

  // 최근 8회 추이 (오래된 → 최신 순으로 표시)
  const trend = [...mockExams].slice(0, 8).reverse();

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-xl font-bold text-gray-800 flex items-center">
          <TrophyIcon className="w-6 h-6 text-yellow-500 mr-2" />
          모의고사 성취도
        </h3>
        <span className={`text-sm font-semibold px-3 py-1 rounded-full ${summary.passLikely ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          {summary.passLikely ? '합격권 (평균 60+)' : '합격 기준 미달'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="text-center p-3 bg-orange-50 rounded-xl">
          <div className="text-2xl font-bold text-orange-600">{summary.latest}점</div>
          <div className="text-xs text-gray-600 mt-1">최근 점수</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-xl">
          <div className="text-2xl font-bold text-green-600">{summary.best}점</div>
          <div className="text-xs text-gray-600 mt-1">최고 점수</div>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-xl">
          <div className="text-2xl font-bold text-blue-600">{summary.average}점</div>
          <div className="text-xs text-gray-600 mt-1">평균 점수</div>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-xl">
          <div className="text-2xl font-bold text-purple-600">{summary.attempts}회</div>
          <div className="text-xs text-gray-600 mt-1">응시 횟수</div>
        </div>
      </div>

      {/* 점수 추이 막대 그래프 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700">점수 추이 (최근 {trend.length}회)</h4>
          <span className="text-xs text-gray-400">— 60점: 합격 기준선</span>
        </div>
        <div className="relative flex items-end justify-between gap-2 h-36 bg-gray-50 rounded-xl p-3 pt-4">
          {/* 합격 기준선 (60점) */}
          <div className="absolute left-3 right-3 border-t-2 border-dashed border-red-300" style={{ bottom: `calc(0.75rem + ${60 * 0.85}%)` }}></div>
          {trend.map((m, i) => {
            const pass = m.score >= 60;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-[11px] font-bold text-gray-700 mb-1">{m.score}</span>
                <div
                  className={`w-full max-w-[34px] rounded-t-md transition-all ${pass ? 'bg-gradient-to-t from-green-500 to-green-400' : 'bg-gradient-to-t from-red-400 to-red-300'}`}
                  style={{ height: `${Math.max(m.score * 0.85, 4)}%` }}
                  title={`${m.date} · ${m.score}점`}
                ></div>
                <span className="text-[10px] text-gray-400 mt-1 truncate w-full text-center">{(m.date || '').slice(5)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// 전체 현황 탭
const OverviewTab = ({ progressData, achievements }) => (
  <div className="space-y-6">
    {/* 모의고사 성취도 */}
    <MockExamAchievementCard mockExams={achievements?.mockExams || []} />

    {/* 전체 진도율 카드 */}
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800">전체 학습 진도</h3>
        <div className="flex items-center text-green-600">
          <ArrowTrendingUpIcon className="w-5 h-5 mr-1" />
          <span className="font-semibold">+12% 이번 주</span>
        </div>
      </div>
      
      <div className="relative mb-4">
        <div className="flex items-center justify-center w-32 h-32 mx-auto">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="transparent"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#3b82f6"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progressData.overallProgress / 100)}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {progressData.overallProgress}%
              </div>
              <div className="text-sm text-gray-500">완료</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-800">
            {Object.values(progressData.examProgress).filter(exam => exam.completed && !exam.isDiagnostic).length}
          </div>
          <div className="text-sm text-gray-600">완료한 회차</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {Math.round(
              Object.values(progressData.examProgress)
                .filter(exam => exam.completed && exam.score && !exam.isDiagnostic)
                .reduce((acc, exam) => acc + exam.score, 0) /
              Object.values(progressData.examProgress).filter(exam => exam.completed && exam.score && !exam.isDiagnostic).length || 0
            )}
          </div>
          <div className="text-sm text-gray-600">평균 점수</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600 flex items-center justify-center">
            <FireIcon className="w-6 h-6 mr-1" />
            {progressData.studyPattern?.consecutiveStudyDays || 0}
          </div>
          <div className="text-sm text-gray-600">연속 학습일</div>
        </div>
      </div>
    </div>

    {/* 최근 학습 현황 */}
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">최근 학습 현황</h3>
      <div className="space-y-3">
        {Object.entries(progressData.examProgress)
          .filter(([examKey]) => !examKey.startsWith('diagnostic_'))  // 진단평가 데이터 비표시
          .sort(([,a], [,b]) => new Date(b.completedAt || b.lastStudied || '1970') - new Date(a.completedAt || a.lastStudied || '1970'))
          .slice(0, 5)
          .map(([examKey, exam]) => {
            // 진단평가인지 확인하고 표시명 변경
            const isDiagnostic = examKey.startsWith('diagnostic_');
            const displayName = isDiagnostic 
              ? `${examKey.replace('diagnostic_', '')} 진단평가`
              : examKey;
              
            return (
              <div key={examKey} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {exam.completed ? (
                    <TrophyIcon className="w-5 h-5 text-yellow-500 mr-3" />
                  ) : isDiagnostic ? (
                    <AcademicCapIcon className="w-5 h-5 text-purple-500 mr-3" />
                  ) : (
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-3" />
                  )}
                  <div>
                    <span className="font-medium">{displayName}</span>
                    {isDiagnostic && (
                      <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                        진단
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {exam.completed ? (
                    <>
                      <div className="font-bold text-green-600">{exam.score}점</div>
                      <div className="text-xs text-gray-500">{exam.completedAt}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-bold text-blue-600">{exam.progress || 0}% 진행</div>
                      <div className="text-xs text-gray-500">
                        {isDiagnostic 
                          ? `${exam.questionsAnswered || 0}/${exam.totalQuestions || 20}문제` 
                          : '진행중'
                        }
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  </div>
);

// 과목별 성취도 섹션 (실전 모의고사 + 과목별 집중 연습 누적)
const SubjectAchievementSection = ({ subjects }) => {
  const certKeys = Object.keys(subjects || {});
  if (certKeys.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
          <AcademicCapIcon className="w-6 h-6 text-indigo-500 mr-2" />
          과목별 성취도
        </h3>
        <p className="text-gray-500 text-sm mt-3">
          아직 과목별 데이터가 없습니다. <span className="font-semibold text-orange-600">과목별 집중 연습</span> 또는 <span className="font-semibold text-orange-600">실전 모의고사</span>를 풀면 과목별 정답률이 집계됩니다.
        </p>
      </div>
    );
  }

  return (
    <>
      {certKeys.map((cert) => {
        const certTitle = examData[cert]?.title || cert;
        // 정답률 낮은 순(취약 과목 우선) 정렬
        const entries = Object.entries(subjects[cert]).sort((a, b) => a[1].accuracy - b[1].accuracy);
        const weakest = entries[0];

        return (
          <div key={cert} className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <AcademicCapIcon className="w-6 h-6 text-indigo-500 mr-2" />
                {certTitle} · 과목별 성취도
              </h3>
            </div>

            {/* 취약 과목 강조 */}
            {weakest && (
              <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-100 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  가장 취약한 과목: <span className="font-bold text-red-600">{weakest[0]}</span>
                  <span className="text-gray-500"> (정답률 {weakest[1].accuracy}%)</span>
                </div>
                <span className="text-xs text-red-500 font-medium">집중 보완 필요</span>
              </div>
            )}

            <div className="space-y-4">
              {entries.map(([subject, s]) => {
                const m = masteryLabel(s.accuracy);
                const c = COLOR_MAP[m.color];
                return (
                  <div key={subject} className="p-4 border border-gray-200 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-800">{subject}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge}`}>{m.label}</span>
                      </div>
                      <span className={`font-bold ${c.text}`}>{s.accuracy}%</span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                      <div className={`${c.bar} h-2.5 rounded-full transition-all duration-700`} style={{ width: `${s.accuracy}%` }}></div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{s.totalCorrect}/{s.totalAnswered}문항 정답 · {s.attempts}회 연습</span>
                      <span>최고 {s.bestAccuracy}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

// 과목별 진도 탭
const SubjectsTab = ({ progressData, achievements }) => (
  <div className="space-y-6">
    {/* 과목별 성취도 (연습 기반) */}
    <SubjectAchievementSection subjects={achievements?.subjects || {}} />

    {/* 과목별 학습 진도 (일반 CBT 풀이 기반) - 데이터 있을 때만 */}
    {Object.keys(progressData.subjectProgress || {}).length > 0 && (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-6">과목별 학습 진도</h3>
      <div className="space-y-4">
        {Object.entries(progressData.subjectProgress || {}).map(([subject, progress]) => {
          const completionRate = (progress.solvedQuestions / progress.totalQuestions) * 100;
          
          return (
            <div key={subject} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-800">{subject}</h4>
                <span className="text-sm text-gray-600">
                  {progress.solvedQuestions}/{progress.totalQuestions} 문제
                </span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${completionRate}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-bold text-green-600">{progress.accuracy}%</div>
                  <div className="text-gray-600">정답률</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-blue-600">{progress.averageTime}분</div>
                  <div className="text-gray-600">평균 시간</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-purple-600">{Math.round(completionRate)}%</div>
                  <div className="text-gray-600">진도율</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
    )}
  </div>
);

// 학습 패턴 탭
const PatternTab = ({ progressData }) => {
  const pattern = progressData.studyPattern || {};
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-6">나의 학습 패턴</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <CalendarDaysIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-600">{pattern.totalStudyDays || 0}</div>
            <div className="text-sm text-gray-600">총 학습일</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <FireIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-600">{pattern.consecutiveStudyDays || 0}</div>
            <div className="text-sm text-gray-600">연속 학습일</div>
          </div>
          
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <ClockIcon className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-orange-600">{pattern.averageSessionTime || 0}</div>
            <div className="text-sm text-gray-600">평균 학습시간(분)</div>
          </div>
          
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <ChartBarIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-purple-600">{pattern.weeklyStudyFrequency || 0}</div>
            <div className="text-sm text-gray-600">주간 학습빈도</div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">학습 선호도 분석</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">선호 학습 시간대</span>
              <span className="font-semibold text-blue-600">{pattern.preferredStudyTime || '분석중'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">최고 성과 시간대</span>
              <span className="font-semibold text-green-600">{pattern.peakPerformanceTime || '분석중'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 목표 관리 탭
const GoalsTab = ({ progressData }) => {
  const goals = progressData.goals || {};
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-6">학습 목표</h3>
        
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">목표 시험일</span>
              <span className="text-blue-600 font-semibold">{goals.targetExamDate || '설정 안됨'}</span>
            </div>
            {goals.targetExamDate && (
              <div className="text-sm text-gray-600">
                D-{Math.max(0, Math.ceil((new Date(goals.targetExamDate) - new Date()) / (1000 * 60 * 60 * 24)))}
              </div>
            )}
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">목표 점수</span>
              <span className="text-green-600 font-semibold">{goals.targetScore || '설정 안됨'}점</span>
            </div>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">일일 문제 목표</span>
              <span className="text-orange-600 font-semibold">{goals.dailyQuestionGoal || '설정 안됨'}문제</span>
            </div>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">주간 학습 목표</span>
              <span className="text-purple-600 font-semibold">{goals.weeklyStudyHours || '설정 안됨'}시간</span>
            </div>
          </div>
        </div>
        
        <button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors">
          목표 수정하기
        </button>
      </div>
    </div>
  );
};

export default LearningProgressTracker;